import { Box, Button, Chip, LinearProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { PlayerInfo, TeamId, toCardCode } from "../interface/GameTypes";
import { PlayingCardFace } from "./PlayingCardFace";

const AUTO_DISMISS_MS = 10000;

interface PlayedCard {
  playerIndex: number;
  card: { suit: string; rank: string };
}

interface Props {
  winnerName: string;
  winnerTeam: TeamId;
  winnerIndex: number;
  round: PlayedCard[];
  players: PlayerInfo[];
  trumpSuit: string | null;
  teamARoundWins: number;
  teamBRoundWins: number;
  onClose: () => void;
}

export const RoundResultModal = ({ winnerName, winnerTeam, winnerIndex, round, players, trumpSuit, teamARoundWins, teamBRoundWins, onClose }: Props) => {
  const [msLeft, setMsLeft] = useState(AUTO_DISMISS_MS);

  useEffect(() => {
    const interval = setInterval(() => {
      setMsLeft((prev) => {
        if (prev <= 100) { clearInterval(interval); onClose(); return 0; }
        return prev - 100;
      });
    }, 100);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teamColor = winnerTeam === TeamId.A ? "#4caf50" : "#ffc107";
  const progress = (msLeft / AUTO_DISMISS_MS) * 100;
  const playerBySeat = (seatIndex: number) => players.find((p) => p.seatIndex === seatIndex);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1200,
        pointerEvents: "all",
        backgroundColor: "rgba(0,0,0,0.35)",
      }}
    >
      <Box
        sx={{
          backgroundColor: "rgba(15,15,30,0.97)",
          border: `2px solid ${teamColor}`,
          borderRadius: 3,
          padding: { xs: "14px 16px", sm: "20px 28px" },
          textAlign: "center",
          minWidth: { xs: 200, sm: 280 },
          maxWidth: { xs: "90vw", sm: 420 },
          boxShadow: `0 0 30px ${teamColor}55`,
        }}
      >
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", letterSpacing: 2, fontSize: "0.65rem" }}>
          ROUND WON
        </Typography>
        <Typography variant="h6" sx={{ color: teamColor, fontWeight: "bold", mt: 0.5, fontSize: { xs: "1rem", sm: "1.25rem" } }}>
          {winnerName}
        </Typography>
        <Chip
          label={`Team ${winnerTeam}`}
          size="small"
          sx={{ backgroundColor: teamColor, color: "#000", fontWeight: "bold", mt: 0.5, mb: 1 }}
        />

        {/* Round wins scoreboard */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 1 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: "#4caf50", fontSize: "0.6rem", letterSpacing: 1 }}>TEAM A</Typography>
            <Typography variant="h6" sx={{ color: "#4caf50", fontWeight: "bold", lineHeight: 1.2 }}>{teamARoundWins}</Typography>
          </Box>
          <Box sx={{ color: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", fontSize: "0.7rem" }}>rounds</Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: "#ffc107", fontSize: "0.6rem", letterSpacing: 1 }}>TEAM B</Typography>
            <Typography variant="h6" sx={{ color: "#ffc107", fontWeight: "bold", lineHeight: 1.2 }}>{teamBRoundWins}</Typography>
          </Box>
        </Box>

        {/* Cards played in this round */}
        <Box sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: { xs: 0.5, sm: 1 },
          mb: 1.5,
          mt: 0.5,
        }}>
          {round.map((pc, i) => {
            const { value, suit } = toCardCode(pc.card);
            const isWinner = pc.playerIndex === winnerIndex;
            const isTrump = trumpSuit != null && pc.card.suit === trumpSuit;
            const name = playerBySeat(pc.playerIndex)?.name ?? `Seat ${pc.playerIndex}`;
            return (
              <Box key={i} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={{
                  border: isWinner ? `2px solid ${teamColor}` : isTrump ? "2px solid #FFD700" : "2px solid transparent",
                  borderRadius: 1,
                  p: "2px",
                  boxShadow: isWinner ? `0 0 8px ${teamColor}88` : "none",
                }}>
                  <PlayingCardFace
                    rankCode={value}
                    suitCode={suit}
                    widthPx={48}
                    sx={{ display: "block" }}
                  />
                </Box>
                <Typography sx={{
                  fontSize: "0.6rem",
                  color: isWinner ? teamColor : "rgba(255,255,255,0.6)",
                  fontWeight: isWinner ? "bold" : "normal",
                  mt: 0.25,
                  maxWidth: 52,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {name}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mb: 1.5,
            borderRadius: 1,
            backgroundColor: `${teamColor}33`,
            "& .MuiLinearProgress-bar": { backgroundColor: teamColor },
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={onClose}
          sx={{
            backgroundColor: teamColor,
            color: "#000",
            fontWeight: "bold",
            "&:hover": { backgroundColor: teamColor, opacity: 0.85 },
          }}
        >
          OK ({Math.ceil(msLeft / 1000)}s)
        </Button>
      </Box>
    </Box>
  );
};
